import { useState, useEffect, useCallback } from 'react';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { securityApi } from '@/services/security';
import { ApiClientError } from '@/services/api';
import { parseAsUTC } from '@/utils/format';
import type { UserDevice } from '@/types';

/**
 * Format relative timestamp for display
 * Shows "2 hours ago", "Yesterday", etc.
 * Note: Timestamps from the backend are in UTC, so we parse them as UTC.
 */
function formatRelativeTime(dateString: string): string {
  const date = parseAsUTC(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  // For older dates, show the full date
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Get device icon based on device name (browser/OS)
 */
function getDeviceIcon(deviceName: string): string {
  const name = deviceName?.toLowerCase() ?? '';

  // Mobile devices
  if (name.includes('iphone') || name.includes('ipad') || name.includes('ios')) {
    return 'mobile-apple';
  }
  if (name.includes('android')) {
    return 'mobile-android';
  }

  // Desktop browsers
  if (name.includes('chrome')) return 'chrome';
  if (name.includes('firefox')) return 'firefox';
  if (name.includes('safari')) return 'safari';
  if (name.includes('edge')) return 'edge';

  // Operating systems
  if (name.includes('windows')) return 'windows';
  if (name.includes('mac') || name.includes('macos')) return 'mac';
  if (name.includes('linux')) return 'linux';

  return 'desktop';
}

/**
 * Device icon component
 */
function DeviceIcon({ deviceName }: { deviceName: string }) {
  const iconType = getDeviceIcon(deviceName);

  // SVG icons for different device types
  const icons: Record<string, JSX.Element> = {
    'mobile-apple': (
      <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    'mobile-android': (
      <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    chrome: (
      <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12a3 3 0 106 0 3 3 0 00-6 0z" />
      </svg>
    ),
    desktop: (
      <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  };

  return icons[iconType] || icons.desktop;
}

export interface DeviceListProps {
  className?: string;
}

/**
 * Device list component for security settings
 * Shows user's registered devices with trust status and management actions
 */
export default function DeviceList({ className }: DeviceListProps) {
  const [devices, setDevices] = useState<UserDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Track if feature is available
  const [isAvailable, setIsAvailable] = useState(true);

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await securityApi.getDevices();
      setDevices(response.devices);
    } catch (err) {
      if (err instanceof ApiClientError) {
        // Hide component if endpoint doesn't exist (404)
        if (err.status === 404) {
          setIsAvailable(false);
          return;
        }
        setError(err.detail);
      } else {
        setError('Failed to load devices.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Handle trust device
  const handleTrustDevice = async (device: UserDevice) => {
    setActionInProgress(device.id);
    setError(null);

    try {
      await securityApi.trustDevice(device.id, { durationDays: 30 });
      setSuccessMessage(`Device "${device.deviceName}" is now trusted for 30 days.`);
      await fetchDevices();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to trust device.');
      }
    } finally {
      setActionInProgress(null);
    }
  };

  // Handle revoke trust
  const handleRevokeTrust = async (device: UserDevice) => {
    setActionInProgress(device.id);
    setError(null);

    try {
      await securityApi.revokeDeviceTrust(device.id);
      setSuccessMessage(`Trust revoked for "${device.deviceName}".`);
      await fetchDevices();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to revoke device trust.');
      }
    } finally {
      setActionInProgress(null);
    }
  };

  // Handle delete device
  const handleDeleteDevice = async (device: UserDevice) => {
    if (device.isCurrent) {
      setError('Cannot remove the current device.');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove "${device.deviceName}" from your devices?`)) {
      return;
    }

    setActionInProgress(device.id);
    setError(null);

    try {
      await securityApi.deleteDevice(device.id);
      setSuccessMessage(`Device "${device.deviceName}" has been removed.`);
      await fetchDevices();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to remove device.');
      }
    } finally {
      setActionInProgress(null);
    }
  };

  // Hide component if feature is not available (404)
  if (!isAvailable) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader
          title="Devices"
          description="Manage devices that have accessed your account"
        />
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader
        title="Devices"
        description="Manage devices that have accessed your account"
      />

      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" className="mb-4" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {devices.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <p>No devices found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {devices.map((device) => (
            <div
              key={device.id}
              className="flex items-start justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
                  <DeviceIcon deviceName={device.deviceName} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {device.deviceName}
                    </span>
                    {device.isCurrent && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Current
                      </span>
                    )}
                    {device.isTrusted && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Trusted
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Last seen: {formatRelativeTime(device.lastSeenAt)}
                  </p>
                  {device.lastLocation && (
                    <p className="text-sm text-gray-500">
                      Location: {device.lastLocation}
                    </p>
                  )}
                  {device.isTrusted && device.trustExpiresAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      Trust expires: {new Date(device.trustExpiresAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!device.isTrusted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTrustDevice(device)}
                    disabled={actionInProgress === device.id}
                    isLoading={actionInProgress === device.id}
                  >
                    Trust
                  </Button>
                )}
                {device.isTrusted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRevokeTrust(device)}
                    disabled={actionInProgress === device.id}
                    isLoading={actionInProgress === device.id}
                  >
                    Revoke
                  </Button>
                )}
                {!device.isCurrent && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDevice(device)}
                    disabled={actionInProgress === device.id}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
